import React, { useState, useMemo } from 'react';
import {
    Search,
    Upload,
    Plus,
    Filter,
    ArrowUpDown,
    Archive,
} from 'lucide-react';
import { TabButton } from '../shared';
import { LeadsTable } from './LeadsTable';
import { LeadDrawer } from '../drawers/LeadDrawer';
import { AddLeadModal } from '../modals/AddLeadModal';
import { ImportModal } from '../modals/ImportModal';

/**
 * Desktop leads view with header, tabs, table, drawers, and modals.
 */
export const DesktopLeads = ({
    activeTab,
    setActiveTab,
    showAddModal,
    setShowAddModal,
    showImportModal,
    setShowImportModal,
    selectedLead,
    showDrawer,
    openLeadDrawer,
    closeDrawer,
    openMenu,
    toggleMenu,
    leads = [],
    lastContactMap = {},
    executionMap = new Map(),
    onAddLead,
    onUpdateStatus,
    onUpdateDetails,
    onArchiveLead,
    onUnarchiveLead,
    onDeleteLead,
    onUpdateTags,
    VALID_STATUSES,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('name'); // 'name', 'lastContact', 'createdAt'
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
    const [showSortMenu, setShowSortMenu] = useState(false);
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [automationFilter, setAutomationFilter] = useState('all');
    const [interestFilter, setInterestFilter] = useState('all');

    // Derive unique automation names from active executions (not from static lead.automation)
    const automationNames = useMemo(() => {
        const names = new Set();
        executionMap.forEach((executions) => {
            executions.forEach(exec => {
                if (exec.automationName) names.add(exec.automationName);
            });
        });
        return [...names].sort();
    }, [executionMap]);

    const propertyAddresses = useMemo(() => {
        const addresses = new Set();
        (leads || []).forEach(lead => {
            (lead.interestedProperties || []).forEach(prop => {
                if (prop.address) addresses.add(prop.address);
            });
        });
        return [...addresses].sort();
    }, [leads]);

    const activeFilterCount = (automationFilter !== 'all' ? 1 : 0) + (interestFilter !== 'all' ? 1 : 0);

    // Enrich leads with last contact data from conversations
    const enrichedLeads = (leads || []).map(lead => {
        const convo = lastContactMap[lead.id];
        return {
            ...lead,
            _lastContactAt: convo?.hasMessages ? convo.lastMessageAt : null,
        };
    });

    // Apply tab filtering
    const tabFilteredLeads = enrichedLeads.filter((lead) => {
        if (activeTab === 'all') return true;
        if (activeTab === 'active') return lead.status === 'Active' && !lead.archived;
        if (activeTab === 'closed') return lead.status === 'Closed' && !lead.archived;
        if (activeTab === 'archived') return lead.archived || lead.status === 'Archived';
        if (activeTab === 'cold') return lead.status === 'Cold' && !lead.archived;
        return true;
    });

    // Apply search filtering
    const searchFilteredLeads = searchQuery.trim() === ''
        ? tabFilteredLeads
        : tabFilteredLeads.filter((lead) => {
            const query = searchQuery.toLowerCase();
            const displayName = (lead.fullName || lead.name || '').toLowerCase();
            return (
                displayName.includes(query) ||
                (lead.email && lead.email.toLowerCase().includes(query)) ||
                (lead.phone && lead.phone.toLowerCase().includes(query)) ||
                (lead.address && lead.address.toLowerCase().includes(query))
            );
        });

    // Apply advanced filters (automation + interest)
    const advancedFilteredLeads = searchFilteredLeads.filter(lead => {
        // Automation filter — uses executionMap (real-time execution data)
        if (automationFilter !== 'all') {
            const leadExecs = executionMap.get(lead.id);
            if (automationFilter === 'none') {
                if (leadExecs && leadExecs.length > 0) return false;
            } else {
                if (!leadExecs || !leadExecs.some(e => e.automationName === automationFilter)) return false;
            }
        }
        // Interest filter
        if (interestFilter !== 'all') {
            if (interestFilter === 'none') {
                if ((lead.interestedProperties || []).length > 0) return false;
            } else {
                const props = lead.interestedProperties || [];
                if (!props.some(p => p.address === interestFilter)) return false;
            }
        }
        return true;
    });

    // Helper to extract timestamp millis from Firestore Timestamp, Date, string, or number
    const toTimestamp = (val) => {
        if (!val) return 0;
        if (typeof val === 'object' && 'seconds' in val) return val.seconds * 1000;
        if (val instanceof Date) return val.getTime();
        if (typeof val === 'string') { const d = new Date(val); return isNaN(d.getTime()) ? 0 : d.getTime(); }
        if (typeof val === 'number') return val;
        return 0;
    };

    // Apply sorting
    const filteredLeads = [...advancedFilteredLeads].sort((a, b) => {
        let aVal, bVal;

        if (sortBy === 'name') {
            aVal = (a.fullName || a.name || '').toLowerCase();
            bVal = (b.fullName || b.name || '').toLowerCase();
        } else if (sortBy === 'lastContact') {
            aVal = toTimestamp(a._lastContactAt);
            bVal = toTimestamp(b._lastContactAt);
        } else if (sortBy === 'createdAt') {
            aVal = toTimestamp(a.createdAt);
            bVal = toTimestamp(b.createdAt);
        }

        if (typeof aVal === 'string') {
            return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        } else {
            return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        }
    });

    const handleSortChange = (newSortBy) => {
        if (sortBy === newSortBy) {
            // Toggle sort order if clicking same sort option
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            // New sort field, default to ascending
            setSortBy(newSortBy);
            setSortOrder('asc');
        }
        setShowSortMenu(false);
    };

    const getSortLabel = () => {
        const labels = {
            name: 'Name',
            lastContact: 'Last Contact',
            createdAt: 'Created Date',
        };
        return `${labels[sortBy]} ${sortOrder === 'asc' ? '↑' : '↓'}`;
    };

    return (
        <>
            <header className="flex-shrink-0 border-b border-notion-border px-8 py-3 flex items-center justify-between bg-white/95 backdrop-blur-sm z-20">
                <div className="flex items-center gap-2 text-sm text-notion-text-light">
                    <span>Workspace</span>
                    <span>/</span>
                    <span className="font-medium text-notion-text">Leads</span>
                </div>
                <div className="flex items-center gap-4">
                    <div className="relative group">
                        <Search className="absolute left-2.5 top-2 w-4 h-4 text-notion-text-light group-hover:text-stone-500 transition-colors" />
                        <input
                            type="text"
                            placeholder="Search leads... (Cmd+K)"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 pr-4 py-1.5 bg-notion-sidebar border border-transparent hover:border-notion-border rounded text-sm focus:outline-none focus:border-stone-300 transition-all w-64 placeholder-notion-text-light text-notion-text"
                        />
                    </div>
                </div>
            </header>

            <div className="px-8 py-6 flex flex-col gap-5 flex-shrink-0">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-semibold text-notion-text tracking-tight">Leads Database</h1>
                    <div className="flex gap-3">
                        <button
                            onClick={() => setShowImportModal(true)}
                            className="flex items-center gap-2 px-3 py-1.5 border border-notion-border rounded text-xs font-medium text-stone-600 hover:bg-notion-sidebar transition-colors"
                        >
                            <Upload className="w-3.5 h-3.5" /> Import
                        </button>
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-stone-800 text-white rounded text-xs font-medium hover:bg-stone-700 transition-colors shadow-sm"
                        >
                            <Plus className="w-3.5 h-3.5" /> Add Lead
                        </button>
                    </div>
                </div>

                <div className="flex items-center gap-1 border-b border-notion-border pb-1">
                    <TabButton active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
                        All Leads
                    </TabButton>
                    <TabButton active={activeTab === 'active'} onClick={() => setActiveTab('active')}>
                        Active
                    </TabButton>
                    <TabButton active={activeTab === 'closed'} onClick={() => setActiveTab('closed')}>
                        Closed
                    </TabButton>
                    <TabButton active={activeTab === 'archived'} onClick={() => setActiveTab('archived')}>
                        <Archive className="w-3 h-3" /> Archived
                    </TabButton>
                    <TabButton active={activeTab === 'cold'} onClick={() => setActiveTab('cold')}>
                        Cold
                    </TabButton>

                    <div className="ml-auto flex items-center gap-2">
                        <div className="relative">
                            <button
                                onClick={() => { setShowFilterMenu(!showFilterMenu); setShowSortMenu(false); }}
                                className={`flex items-center gap-1 px-2 py-1 hover:bg-notion-hover rounded text-xs transition-colors ${activeFilterCount > 0 ? 'text-stone-900 font-medium' : 'text-notion-text-light'}`}
                            >
                                <Filter className="w-3.5 h-3.5" /> Filter
                                {activeFilterCount > 0 && (
                                    <span className="ml-1 bg-stone-800 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                                        {activeFilterCount}
                                    </span>
                                )}
                            </button>
                            {showFilterMenu && (
                                <div className="absolute right-0 mt-1 bg-white border border-notion-border rounded-lg shadow-lg z-50 w-64 p-3 space-y-3">
                                    <div>
                                        <label className="block text-[11px] font-semibold text-stone-400 uppercase tracking-wider mb-1">Automation</label>
                                        <select
                                            value={automationFilter}
                                            onChange={(e) => setAutomationFilter(e.target.value)}
                                            className="w-full text-sm border border-notion-border rounded px-2 py-1.5 bg-white text-stone-700 focus:outline-none focus:border-stone-400"
                                        >
                                            <option value="all">All automations</option>
                                            <option value="none">No automation</option>
                                            {automationNames.map(name => (
                                                <option key={name} value={name}>{name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-semibold text-stone-400 uppercase tracking-wider mb-1">Interest</label>
                                        <select
                                            value={interestFilter}
                                            onChange={(e) => setInterestFilter(e.target.value)}
                                            className="w-full text-sm border border-notion-border rounded px-2 py-1.5 bg-white text-stone-700 focus:outline-none focus:border-stone-400"
                                        >
                                            <option value="all">All interests</option>
                                            <option value="none">No interest</option>
                                            {propertyAddresses.map(addr => (
                                                <option key={addr} value={addr}>{addr}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {activeFilterCount > 0 && (
                                        <button
                                            onClick={() => { setAutomationFilter('all'); setInterestFilter('all'); }}
                                            className="w-full text-xs text-stone-500 hover:text-stone-700 py-1 transition-colors"
                                        >
                                            Clear all filters
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="relative">
                            <button
                                onClick={() => { setShowSortMenu(!showSortMenu); setShowFilterMenu(false); }}
                                className="flex items-center gap-1 px-2 py-1 hover:bg-notion-hover rounded text-xs text-notion-text-light transition-colors"
                            >
                                <ArrowUpDown className="w-3.5 h-3.5" /> Sort
                            </button>
                            {showSortMenu && (
                                <div className="absolute right-0 mt-1 bg-white border border-notion-border rounded shadow-lg z-50 w-48">
                                    <button
                                        onClick={() => handleSortChange('name')}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-notion-hover transition-colors ${sortBy === 'name' ? 'font-semibold text-stone-900' : 'text-stone-700'
                                            }`}
                                    >
                                        Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                                    </button>
                                    <button
                                        onClick={() => handleSortChange('lastContact')}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-notion-hover transition-colors ${sortBy === 'lastContact' ? 'font-semibold text-stone-900' : 'text-stone-700'
                                            }`}
                                    >
                                        Last Contact {sortBy === 'lastContact' && (sortOrder === 'asc' ? '↑' : '↓')}
                                    </button>
                                    <button
                                        onClick={() => handleSortChange('createdAt')}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-notion-hover transition-colors ${sortBy === 'createdAt' ? 'font-semibold text-stone-900' : 'text-stone-700'
                                            }`}
                                    >
                                        Created Date {sortBy === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-8 pb-8">
                <LeadsTable
                    leads={filteredLeads}
                    executionMap={executionMap}
                    openLeadDrawer={openLeadDrawer}
                    openMenu={openMenu}
                    toggleMenu={toggleMenu}
                    onArchiveLead={onArchiveLead}
                    onUnarchiveLead={onUnarchiveLead}
                    onDeleteLead={onDeleteLead}
                />
            </div>

            {showDrawer && (
                <>
                    <div className="fixed inset-0 bg-stone-900/10 backdrop-blur-sm z-40 transition-opacity" onClick={closeDrawer} />
                    <LeadDrawer
                        lead={selectedLead}
                        onClose={closeDrawer}
                        onUpdateStatus={onUpdateStatus}
                        onUpdateDetails={onUpdateDetails}
                        onUpdateTags={onUpdateTags}
                        onArchiveLead={onArchiveLead}
                        onUnarchiveLead={onUnarchiveLead}
                        onDeleteLead={onDeleteLead}
                        leads={leads}
                        VALID_STATUSES={VALID_STATUSES}
                    />
                </>
            )}

            {showAddModal && <AddLeadModal onClose={() => setShowAddModal(false)} onAddLead={onAddLead} leads={leads} />}
            {showImportModal && <ImportModal onClose={() => setShowImportModal(false)} onImportComplete={() => setShowImportModal(false)} />}
        </>
    );
};

export default DesktopLeads;
